import { Database, Info, Lightbulb } from 'lucide-react'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { cn } from '../lib/utils'
import { Badge } from './ui'

export interface ChartProvenanceCalloutProps {
  sourceName: string
  sourceType?: 'real' | 'modeled' | 'mixed'
  sourceDesc?: string
  insight: string
  className?: string
  defaultOpen?: boolean
}

/**
 * Micro-componente reutilizable para dotar a los gráficos y mapas del Data Explorer
 * de una explicación clara, visual y profesional sobre:
 * 1. ¿De dónde sale el dato? (Fuente oficial, trazabilidad ETDS, dato real vs modelo)
 * 2. ¿Qué nos está diciendo? (Lectura de negocio y toma de decisiones para PYMEs)
 */
export function ChartProvenanceCallout({
  sourceName,
  sourceType = 'real',
  sourceDesc,
  insight,
  className,
  defaultOpen = false,
}: ChartProvenanceCalloutProps) {
  const { t } = useTranslation()
  const [isOpen, setIsOpen] = useState(defaultOpen)

  const typeConfig = {
    real: {
      label: t('chartExpl.typeReal', 'Dato Real Medido'),
      badgeVariant: 'success' as const,
      border: 'border-emerald-500/30',
      bg: 'bg-emerald-500/5',
      text: 'text-emerald-400',
    },
    modeled: {
      label: t('chartExpl.typeModeled', 'Modelo Analítico'),
      badgeVariant: 'warning' as const,
      border: 'border-amber-500/30',
      bg: 'bg-amber-500/5',
      text: 'text-amber-400',
    },
    mixed: {
      label: t('chartExpl.typeMixed', 'Híbrido (Real + Calibrado)'),
      badgeVariant: 'outline' as const,
      border: 'border-sky-500/30',
      bg: 'bg-sky-500/5',
      text: 'text-sky-400',
    },
  }[sourceType]

  return (
    <div
      className={cn(
        'mt-3.5 rounded-xl border bg-muted/20 p-2.5 transition-all duration-200',
        isOpen ? 'border-border/80 bg-muted/40' : 'border-border/40 hover:border-border/70',
        className
      )}
    >
      <button
        type="button"
        onClick={() => setIsOpen((prev) => !prev)}
        className="flex w-full items-center justify-between gap-2 text-left text-xs"
        aria-expanded={isOpen}
      >
        <div className="flex flex-wrap items-center gap-2 min-w-0">
          <span className="flex items-center gap-1.5 font-semibold text-foreground/90">
            <Info className="size-3.5 text-primary shrink-0" />
            <span>{t('chartExpl.headerTitle', 'Cómo interpretar este gráfico')}</span>
          </span>
          <Badge
            variant={typeConfig.badgeVariant}
            className="text-[9px] font-mono uppercase tracking-wider py-0 px-1.5"
          >
            {typeConfig.label}
          </Badge>
          <span className="hidden sm:inline-block text-[11px] text-muted-foreground truncate">
            · {sourceName}
          </span>
        </div>

        <span className="text-[10px] font-medium text-primary hover:underline shrink-0">
          {isOpen ? t('chartExpl.hide', 'Ocultar') : t('chartExpl.show', 'Ver procedencia y claves')}
        </span>
      </button>

      {isOpen && (
        <div className="mt-2.5 pt-2.5 border-t border-border/50 grid grid-cols-1 md:grid-cols-12 gap-3 text-xs leading-relaxed animate-in fade-in-50 duration-200">
          {/* Columna 1: De dónde sale el dato */}
          <div className="md:col-span-5 rounded-lg border border-border/50 bg-background/50 p-2.5 space-y-1">
            <div className="flex items-center gap-1.5 font-semibold text-foreground/90 text-[11px] uppercase tracking-wide">
              <Database className="size-3.5 text-sky-400 shrink-0" />
              <span>{t('chartExpl.sourceTitle', '¿De dónde salen los datos?')}</span>
            </div>
            <p className="font-medium text-sky-400/90 text-xs">{sourceName}</p>
            {sourceDesc && (
              <p className="text-[11px] text-muted-foreground">{sourceDesc}</p>
            )}
          </div>

          {/* Columna 2: Qué nos está diciendo (Insight para PYMEs) */}
          <div className="md:col-span-7 rounded-lg border border-amber-500/20 bg-amber-500/5 p-2.5 space-y-1">
            <div className="flex items-center gap-1.5 font-semibold text-amber-400 text-[11px] uppercase tracking-wide">
              <Lightbulb className="size-3.5 text-amber-400 shrink-0" />
              <span>{t('chartExpl.insightTitle', '¿Qué nos está diciendo?')}</span>
            </div>
            <p className="text-foreground/90 text-xs font-normal">{insight}</p>
          </div>
        </div>
      )}
    </div>
  )
}
